import React, { useState, useEffect, lazy, Suspense } from 'react';
import TitleHelmet from 'components/TitleHelmet';
import Syringe from '../components/SyringeSVG';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryParams } from 'components/hooks/useQueryParams';
import Disclaimer from '../components/Disclaimer';

import { CalculatorInput, CalculatorWarning } from 'components/calc';
import { useCalculatorStorage } from 'components/hooks/useCalculatorStorage';
import Analytics from '../services/analytics';
import { DoseTermsExplainerSkeleton, DosageTableSkeleton, OtherCalcsSkeleton } from '../components/LoadingSkeletons';

// Lazy load non-critical components
const DoseTermsExplainer = lazy(() => import('../components/DoseTermsExplainer'));
const DosageTable = lazy(() => import('components/DoseTableCheatSheet'));
const OtherCalcs = lazy(() => import('components/OtherCalcs'));

const DosageCalculator: React.FC = () => {
  const [showNote, setShowNote] = useState<boolean>(false);
  const { concentration: queryConcentration, desiredDosage: queryDosage } = useQueryParams();
  const navigate = useNavigate();

  // Use the custom storage hook
  const [concentration, setConcentration] = useCalculatorStorage({
    key: 'concentration',
    initialValue: '',
    queryValue: queryConcentration || undefined,
  });

  const [desiredDosage, setDesiredDosage] = useCalculatorStorage({
    key: 'desiredDosage',
    initialValue: '',
    queryValue: queryDosage || undefined,
  });

  const [calculatedVolume, setCalculatedVolume] = useState<number | null>(null);
  const [showInputs, setShowInputs] = useState<boolean>(true);

  const handleCalculate = (): void => {
    const concentrationValue = parseFloat(concentration);
    const desiredDosageValue = parseFloat(desiredDosage);

    if (concentrationValue > 0 && desiredDosageValue > 0) {
      const volume = desiredDosageValue / concentrationValue;
      setCalculatedVolume(volume);
      setShowInputs(false);

      // Scroll to top when calculation is submitted for better UX
      window.scrollTo({ top: 0, behavior: 'smooth' });

      Analytics.trackCalculation('Standard', {
        concentration: concentrationValue,
        desired_dosage: desiredDosageValue,
        result: volume,
      });
    } else {
      alert('Please enter valid values for both concentration and desired dosage');
    }
  };

  const handleShowInputs = (): void => {
    setShowInputs(true);
    setCalculatedVolume(null); // Reset the calculation results
  };

  return (
    <>
      <div className="App">
        <TitleHelmet
          title="Tirzepatide Dose Calculator"
          description="Simple calculator to help you figure out how many units of tirzepatide (Mounjaro/Zepbound) you need to take based on your desired dosage and the concentration of the medication in your vial."
        />

        <header className="App-header">
          <h1>Tirzepatide Dose Calculator</h1>
        </header>
        {showInputs ? (
          <div
            className="calculator-container"
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter') handleCalculate();
            }}
          >
            <div className="input-container">
              <p>
                Need help figuring out dosing for your tirzepatide (Mounjaro/Zepbound) prescription? You've come to the
                right place! You just need the concentration in your vial (listed as mg/mL) and the dose in mg you want.
              </p>

              {/* Use CalculatorInput component */}
              <CalculatorInput
                id="concentration"
                value={concentration}
                onChange={setConcentration}
                unit="mg/mL"
                placeholder="e.g., 10"
                label="Concentration (mg/mL)"
                onClick={() => setShowNote(!showNote)}
                isClickable={true}
              />

              {showNote && (
                <div className="tooltip">
                  <p>
                    Check your prescription label for the concentration. It should be listed as something like 'X
                    mg/mL'. If your medication has an additional additive, like a vitamin B12, for instance, it might
                    list 2 concentrations, like "17/2 mg/mL" -- this means 17mg/mL of the primary medication (e.g.
                    Tirzepatide) and 2mg/mL of the additive. You can ignore the additive, and just enter the first
                    number.
                  </p>
                  <p>
                    Also note, if your concentration is something like 5mg/0.5mL, that's the equivalent of 10mg/mL
                    (double the first number.)
                  </p>
                  <p>
                    <Link to="/faq/finding-concentration" className="help-link">
                      Need more help finding your concentration? Click here for our detailed guide with examples.
                    </Link>
                  </p>
                  <span onClick={() => setShowNote(false)} className="close-tooltip">
                    ✖
                  </span>
                </div>
              )}

              {/* Use CalculatorInput component */}
              <CalculatorInput
                id="desiredDosage"
                value={desiredDosage}
                onChange={setDesiredDosage}
                unit="mg"
                placeholder="e.g., 2.5"
                label="Desired Dosage (mg)"
              />
            </div>
            <button onClick={handleCalculate} className="calculate-button">
              Calculate
            </button>
            <Link to="/faq/finding-concentration" className="help-link">
              Click here to learn more about how to read your concentration from your label.
            </Link>
          </div>
        ) : (
          <span>
            &#9664;{' '}
            <Link className="smaller-text" onClick={handleShowInputs} to="">
              Reset calculator and start again
            </Link>
          </span>
        )}
        {calculatedVolume !== null && (
          <div className="result centered">
            <div>
              {parseFloat(concentration) <= 2 ? (
                <CalculatorWarning
                  type="urgent"
                  message={
                    <>
                      A concentration of {concentration}mg/mL is extremely low and likely incorrect. Normal
                      concentrations for tirzepatide/semaglutide are between 7-25mg/mL. Please{' '}
                      <Link to="/faq/finding-concentration">check our guide on how to read your concentration</Link> and
                      verify your vial label carefully before proceeding.
                    </>
                  }
                />
              ) : (
                <>
                  {parseFloat(concentration) <= 6 && (
                    <CalculatorWarning
                      type="urgent"
                      message={`The concentration of ${concentration}mg/mL is unusually low. Normal concentration for
                      tirzepatide is generally between 7mg/mL and 30mg/mL. Please double-check your vial label to confirm
                      this concentration is correct to avoid the risk of overdose.`}
                    />
                  )}
                  {parseFloat(concentration) > 30 && (
                    <CalculatorWarning
                      message={`The concentration of ${concentration}mg/mL is unusually high. Normal concentration for
                      tirzepatide is generally between 7mg/mL and 30mg/mL. Please double-check your vial label to confirm
                      this concentration is correct.`}
                    />
                  )}
                  For {desiredDosage}mg at {concentration}mg/mL concentration,
                  {
                    <div className="resultsText">
                      you'd need {Math.round(calculatedVolume * 100)} units
                      <br />
                    </div>
                  }
                  on a typical U-100 syringe.
                  {calculatedVolume > 1 && <span> (This will probably require multiple syringes.)</span>}
                  {parseFloat(desiredDosage) > 15 && (
                    <CalculatorWarning
                      type="urgent"
                      message={`Doses above 15mg are unusually high. Please verify your prescription and calculations
                      carefully.`}
                    />
                  )}
                  <Syringe calculatedVolume={calculatedVolume} />
                  <Suspense fallback={<DosageTableSkeleton />}>
                    <DosageTable
                      concentration={concentration}
                      targetDosage={desiredDosage}
                      medicationType="tirzepatide"
                    />
                  </Suspense>
                </>
              )}
            </div>
          </div>
        )}
        <Disclaimer />

        <Suspense fallback={<OtherCalcsSkeleton />}>
          <OtherCalcs />
        </Suspense>

        <Suspense fallback={<DoseTermsExplainerSkeleton />}>
          <DoseTermsExplainer />
        </Suspense>
      </div>
    </>
  );
};

export default DosageCalculator;
